import { Button, Flex, Result, Spin } from "antd"
import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react"
import { useTranslation } from "react-i18next"

import { DownloadCopy } from "../../../components/Esign/signing/DownloadCopy.tsx"
import { SignedPanel } from "../../../components/Esign/signing/SignedPanel.tsx"
import { LazySignDocument } from "../../../components/Esign/signing/index.ts"
import type {
  Outcome,
  SigningState
} from "../../../components/Esign/signing/index.ts"
import {
  certificateName,
  signingIsOpen
} from "../../../components/Esign/signing/types.ts"
import { createPublicTransport } from "../api/signing.transport"
import type { RefusalReason, SigningRefusal } from "../types/signing.types"
import SigningDocumentView from "./SigningDocumentView"

interface Props {
  session: string
  documentUrl: string | null
  documentBlob: Blob | null
  documentName: string
  recipientName: string
  isSigner: boolean
  completed: boolean
  onMaySign: (maySign: boolean) => void
  onDeclined: () => void
  onRefused: (refusal: SigningRefusal) => void
}

type Ending = "signing" | "signed" | "later"

// Signed is every signature in; completed is sealed as well. Either way nobody else is asked.
const EVERYONE_SIGNED = ["signed", "completed"]

// A dead link, as opposed to one action refused, which the signing screen words itself.
const DEAD_LINK: readonly RefusalReason[] = [
  "envelope_not_live",
  "expired",
  "revoked",
  "consent_withdrawn",
  "parent_archived"
]

const deadLink = (outcome: Outcome<unknown>): SigningRefusal | null => {
  const reason = DEAD_LINK.find((known) => known === outcome.reason)
  return reason ? { reason, detail: outcome.detail ?? undefined } : null
}

const DocumentStep: React.FC<Props> = ({
  session,
  documentUrl,
  documentBlob,
  documentName,
  recipientName,
  isSigner,
  completed,
  onMaySign,
  onDeclined,
  onRefused
}) => {
  const { t } = useTranslation()
  const [ending, setEnding] = useState<Ending>("signing")
  const [everyoneSigned, setEveryoneSigned] = useState(false)
  // Read once: a later copy must not build a second transport, which would fetch again.
  const held = useRef(documentBlob)
  const transport = useMemo(
    () => createPublicTransport(session, held.current),
    [session]
  )

  // A new session is a new sitting, even when the step itself stays mounted.
  useEffect(() => {
    setEnding("signing")
  }, [session])

  const download = useCallback(
    () => transport.download(documentName),
    [transport, documentName]
  )

  const downloadCertificate = useCallback(
    () => transport.downloadCertificate(certificateName(documentName)),
    [transport, documentName]
  )

  const onStateLoaded = useCallback(
    (state: SigningState) => onMaySign(signingIsOpen(state)),
    [onMaySign]
  )

  const onFailed = useCallback(
    (outcome: Outcome<unknown>) => {
      const refusal = deadLink(outcome)
      if (refusal) onRefused(refusal)
    },
    [onRefused]
  )

  if (isSigner) {
    if (ending === "signed") {
      return (
        <SignedPanel
          title={t(
            everyoneSigned
              ? "signing.sign.everyoneSignedTitle"
              : "signing.sign.signedTitle"
          )}
          documentName={documentName}
          body={t(
            everyoneSigned
              ? "signing.sign.everyoneSignedBody"
              : "signing.sign.stepDoneBody"
          )}
          actions={
            <DownloadCopy
              download={download}
              label={t("signing.sign.download")}
            />
          }
        />
      )
    }

    // Kept mounted under the notice: reading the document again is an audited opening.
    return (
      <>
        {ending === "later" && (
          <Result
            status="info"
            title={t("signing.later.title")}
            subTitle={t("signing.later.body")}
            extra={
              <Button onClick={() => setEnding("signing")}>
                {t("signing.later.back")}
              </Button>
            }
          />
        )}
        <div hidden={ending === "later"}>
          <Suspense
            fallback={
              <Flex justify="center" style={{ padding: "48px 0" }}>
                <Spin size="large" />
              </Flex>
            }
          >
            <LazySignDocument
              transport={transport}
              signerName={recipientName}
              documentName={documentName}
              onStateLoaded={onStateLoaded}
              onFailed={onFailed}
              onSigned={(result) => {
                onMaySign(false)
                setEveryoneSigned(
                  EVERYONE_SIGNED.includes(result.envelope_status)
                )
                setEnding("signed")
              }}
              // Every field and the adopted mark are saved as they are applied; only
              // the session lives in this tab, so coming back means the emailed link.
              onSaveForLater={() => setEnding("later")}
              onDeclined={onDeclined}
            />
          </Suspense>
        </div>
      </>
    )
  }

  if (!documentUrl) {
    return (
      <Flex justify="center" style={{ padding: "48px 0" }}>
        <Spin size="large" />
      </Flex>
    )
  }

  return (
    <Flex vertical gap={16}>
      <SigningDocumentView
        session={session}
        documentBlob={documentBlob}
        height="60vh"
      />
      <DownloadCopy
        download={download}
        label={t("signing.document.download")}
      />
      {completed && (
        <DownloadCopy
          download={downloadCertificate}
          label={t("signing.certificate.download")}
          failedText={t("signing.certificate.failed")}
        />
      )}
    </Flex>
  )
}

export default DocumentStep
